import { Text, Typeahead } from "@zapier/design-system";
import { SyntheticEvent, useCallback, useState } from "react";
import {
  Actor,
  actorsAtomFamily,
  nodeAtomFamily,
  takeSnapshotAtom,
  DefaultNodeData,
  ZapNodeData,
} from "@zapier/canvas-state";
import { useAtomValue, useSetAtom } from "jotai";
import { Node } from "@xyflow/react";

import useUpdateNode from "../../../hooks/useUpdateNode";
import { generateActorColor } from "../../../utils/generateActorColor";
import { ACTOR_TYPEAHEAD_LABEL } from "@zapier/canvas-constants";

export function ActorTypeahead({
  isDisabled,
  id,
}: {
  isDisabled?: boolean;
  id: string;
}) {
  const node = useAtomValue(nodeAtomFamily(id)) as Node<
    DefaultNodeData | ZapNodeData
  >;
  const [search, setSearch] = useState<string>("");
  const allActors = useAtomValue(actorsAtomFamily(false));
  const actor = node?.data.actors?.[0] ?? null;

  const updateNodeAndSave = useUpdateNode();
  const takeSnapshot = useSetAtom(takeSnapshotAtom);

  // @ts-ignore
  const setClearSelection = (f) => (clearSelection = f);
  let clearSelection = useCallback(
    () => {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setClearSelection],
  );

  function handleSubmit(item?: Actor | null | undefined) {
    const trimmedSearchTerm = search.trim();

    // Selecting an option from Typeahead
    if (item) {
      takeSnapshot();

      updateNodeAndSave({
        id,
        data: {
          actors: [item],
        },
      });
    } else {
      // Typing and pressing enter or on blur
      if (trimmedSearchTerm) {
        takeSnapshot();

        const existingActorInTypeahead = allActors.find(
          (actor) => actor.label === trimmedSearchTerm,
        );

        updateNodeAndSave({
          id,
          data: {
            actors: [
              existingActorInTypeahead ?? {
                label: trimmedSearchTerm,
                color: actor?.color ?? generateActorColor(trimmedSearchTerm),
              },
            ],
          },
        });
      }
    }

    clearSelection();
    setSearch("");
  }

  function onKeyDown(event: SyntheticEvent) {
    const keyboardEvent = event as React.KeyboardEvent<HTMLInputElement>;
    if (keyboardEvent.key === "Enter") {
      handleSubmit();
    }
  }

  function handleInputChange(inputValue: string) {
    setSearch(inputValue);
  }

  return (
    <>
      <Text type="SmallPrint2">{ACTOR_TYPEAHEAD_LABEL}</Text>
      <Typeahead
        key={actor?.label} // Need this or else if you clear and select the same item, sometimes it won't select
        placeholder=""
        menuAriaLabel="List of actors"
        aria-label="Typeahead to choose an actor"
        autoComplete="off"
        getLabelForItem={(item: any) => item.label}
        items={allActors}
        onChange={handleSubmit}
        onBlur={() => handleSubmit()}
        onKeyDown={onKeyDown}
        openOnFocus={true}
        keepInputValueOnBlur={true}
        onInputValueChange={handleInputChange}
        isDisabled={isDisabled}
      />
    </>
  );
}
